import { useMemo } from 'react'
import { Cell, Column } from 'react-table'
import { Button, Flex, Text, Badge } from '@ritualco/jutsu'
import { useDeleteWorkspaceAdmin } from 'api/endpoints/portalAdmins'
import { BasicTable } from 'components/BasicTable'
import { useContent } from 'lib/content/react'
import { TeamMember } from 'types/entities'
import { PortalAdminRole, PortalAdminStatus } from 'lib/generated/api'
import { useStore } from 'state/store'

export type TeamTableProps = {
  members?: TeamMember[]
  onRemove?: (portalAdminId: string) => void
}

export const TeamTable = ({
  members = [],
  onRemove = () => {},
}: TeamTableProps) => {
  const { formatMessage } = useContent()
  const userInfo = useStore(state => state.userInfo)

  const columns = useMemo<Column<TeamMember>[]>(
    () => [
      {
        Header: formatMessage({ id: 'pmportal.settings.team.user' }),
        accessor: 'email',
        Cell: props => (
          <Flex direction="column">
            <Text>{props.row.original.name}</Text>
            <Text color="grayscale.secondary">{props.row.original.email}</Text>
          </Flex>
        ),
      },
      {
        id: 'role',
        Header: formatMessage({ id: 'pmportal.settings.team.role' }),
        accessor: row => {
          let roleLabel
          switch (row.role) {
            case PortalAdminRole.Administrator:
              roleLabel = formatMessage({
                id: 'pmportal.settings.team.adminLabel',
              })
              break
            case PortalAdminRole.Owner:
              roleLabel = formatMessage({
                id: 'pmportal.settings.team.ownerLabel',
              })
              break
            case PortalAdminRole.ViewOnly:
              roleLabel = formatMessage({
                id: 'pmportal.settings.team.viewerLabel',
              })
              break
            default:
              roleLabel = '-'
          }
          return roleLabel
        },
      },
      {
        Header: formatMessage({ id: 'pmportal.settings.team.status' }),
        accessor: 'status',
        Cell: ({ value }) => {
          if (value === PortalAdminStatus.Invited) {
            return (
              <Badge colorScheme="gray">
                {formatMessage({ id: 'pmportal.settings.team.invitedStatus' })}
              </Badge>
            )
          } else if (value === PortalAdminStatus.Revoked) {
            return (
              <Badge colorScheme="red" variant="outline">
                {formatMessage({ id: 'pmportal.settings.team.deletedStatus' })}
              </Badge>
            )
          } else if (value === PortalAdminStatus.Active) {
            return (
              <Badge>
                {formatMessage({ id: 'pmportal.settings.team.acceptedStatus' })}
              </Badge>
            )
          }

          return '-'
        },
      },
      {
        id: 'actions',
        Cell: ({ row }: Cell<TeamMember>) => {
          const { portalAdminId, role, status } = row.original

          if (
            portalAdminId &&
            members.length > 1 &&
            role !== PortalAdminRole.Owner &&
            userInfo.portalAdminId !== portalAdminId &&
            status !== PortalAdminStatus.Revoked
          ) {
            return (
              <Button
                size="sm"
                colorScheme="transparent"
                variant="outline"
                float="right"
                onClick={() => {
                  onRemove(portalAdminId)
                }}
              >
                {formatMessage({ id: 'pmportal.global.actionRemove' })}
              </Button>
            )
          }
          return null
        },
      },
    ],
    [formatMessage, members.length, onRemove, userInfo.portalAdminId]
  )

  return (
    <BasicTable
      columns={columns}
      data={members}
      tableProps={() => ({ style: { tableLayout: 'fixed' } })}
      tdProps={() => ({ lineHeight: 'inherit' })}
    />
  )
}
